import { Suspense, useEffect, useMemo, useState } from "react";
import Loading from "../../../../common/components/ui/loading/Loading";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import {
  budgetRefreshMaintenance,
  editWaitingTicketAndMechanic,
  getTicketStock,
  TypeBudgetGurmet,
} from "../../../../common/services/maintenance.api";
import PopUpDelete from "../../../../common/components/layout/partials/PopupDelete";
import { HiClipboardCheck } from "react-icons/hi";
import LoadingOverlay from "../../../../common/components/ui/loading/LoadingOverlay";
import { getMechanicOptions } from "../../../../common/helpers/mechanic";
import { useCompanyStore } from "../../../../common/states/store/Company";

const headers = {
  code: "Senha",
  licensePlate: "Placa",
  mechanic: "Mecânico",
  totalItems: "Qtd. Itens",
  action: "Ações",
};

type UniquePlate = {
  code: string;
  licensePlate: string;
  mechanic: string;
  totalItems: number;
};

function TableTicketStock() {
  const [openPlate, setOpenPlate] = useState<string | null>(null);
  const { notifyError, notifySuccess } = useToastContext();
  const [, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [tickets, setTickets] = useState<
    { name: string; code: string; quantity: number }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingNew, setLoadingNew] = useState(false);
  const [mechanic, setMechanic] = useState<string | null>(null);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });
  const { filterMode, selectedCompanies } = useCompanyStore();
  const singleSelectedCompany = useMemo(() => {
    if (filterMode === "cities" && selectedCompanies.length === 1) {
      return selectedCompanies[0];
    }
    return null;
  }, [filterMode, selectedCompanies]);
  const mechanicOptions = getMechanicOptions(
    singleSelectedCompany?.companyName || ""
  );

  const processStockWithdrawal = async (item) => {
    try {
      const data: TypeBudgetGurmet = {
        maintenanceId: item.maintenanceId,
        _id: item._id,
        code: item.code,
        name: item.name,
        quantity: item.quantity,
        licensePlate: item.licensePlate,
        mechanic: item.mechanic,
        isRemovedFromStock: true,
        actionStatus: item.actionStatus,
      };
      const result = await budgetRefreshMaintenance(data);
      if (result.ok) return { success: true, message: result.message };
      return { success: false, message: result.message };
    } catch (error) {
      return {
        success: false,
        message: "Erro inesperado ao processar a retirada.",
      };
    }
  };

  async function loadTicketStock() {
    setTickets([]);
    setLoading(true);
    const result = await getTicketStock(filters.search);
    setData({ itemsPerPage: 10, totalItems: result.data.length });
    setLoading(false);

    if (!result.ok) {
      notifyError((result as { message?: string })?.message || "unknown error");
      return;
    }
    const filteredResult = result.data.filter(
      (item) => item.isRemovedFromStock !== true
    );
    setTickets(filteredResult);
  }

  useEffect(() => {
    const fetchData = async () => {
      await loadTicketStock();
    };
    fetchData();
    // eslint-disable-next-line
  }, [filters]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ticketsByPlate = tickets.reduce((acc, item: any) => {
    if (!acc[item.licensePlate]) acc[item.licensePlate] = [];
    acc[item.licensePlate].push(item);
    return acc;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, {} as Record<string, any[]>);

  const uniquePlates: UniquePlate[] = Object.keys(ticketsByPlate).map(
    (plate) => {
      const firstItem = ticketsByPlate[plate][0];
      return {
        code: firstItem.waitingTicket || firstItem.code,
        licensePlate: plate,
        mechanic: firstItem.mechanic || "Não informado",
        totalItems: ticketsByPlate[plate].length,
      };
    }
  );
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Retirada de Estoque"
          placeholder="Código,Peça, Senha, Mecânico, Placa..."
          statusOptions={{}}
          onNewItemClick={() => {}}
          onSearch={handleFilterChange}
          entity="inventory-suppliers"
          tableData={tickets}
        />
        <GenericTable
          headers={headers}
          data={uniquePlates}
          renders={{
            action: (_val: unknown, context: unknown) => {
              const row = context as UniquePlate;
              return (
                <div className="flex justify-center">
                  <button
                    onClick={() => {
                      setOpenPlate(row.licensePlate);
                      setMechanic("");
                    }}
                    className="flex items-center justify-center gap-2 px-2 py-1 rounded-md hover:bg-red-600 transition-all duration-200 shadow-sm"
                    style={{
                      cursor: "pointer",
                      width: "130px",
                      fontSize: "12px",
                      backgroundColor: "rgb(228, 65, 49)",
                    }}
                  >
                    <HiClipboardCheck className="w-4 h-4 text-white" />
                    <span className="font-medium text-white">Baixar item</span>
                  </button>

                  {openPlate === row.licensePlate && (
                    <PopUpDelete
                      disabled={
                        !mechanic &&
                        ticketsByPlate[row.licensePlate].some(
                          (i) => !i.mechanic
                        )
                      }
                      anotherText={
                        <div className="flex flex-col gap-4 w-full">
                          <h3 className="text-lg font-bold text-red-600">
                            Itens da placa {row.licensePlate}
                          </h3>

                          <div className="w-full border border-gray-200 rounded-lg overflow-hidden">
                            <div className="grid grid-cols-3 bg-gray-50 border-b border-gray-200">
                              <div className="px-3 py-2 text-sm font-semibold">
                                Código
                              </div>
                              <div className="px-3 py-2 text-sm font-semibold">
                                Descrição
                              </div>
                              <div className="px-3 py-2 text-sm font-semibold text-center">
                                Quantidade
                              </div>
                            </div>

                            {ticketsByPlate[row.licensePlate].map((item) => (
                              <div
                                key={item._id}
                                className="grid grid-cols-3 border-b last:border-b-0 border-gray-200"
                              >
                                <div className="px-3 py-2 text-sm break-words max-w-[100px] sm:max-w-none">
                                  {item.code}
                                </div>
                                <div className="px-3 py-2 text-sm break-words max-w-[120px] sm:max-w-none">
                                  {item.name}
                                </div>
                                <div className="px-3 py-2 text-sm text-center break-words">
                                  {item.quantity}
                                </div>
                              </div>
                            ))}
                          </div>

                          <section id="selected-mechanic" className="mb-10">
                            <select
                              onChange={(e) => setMechanic(e.target.value)}
                              value={mechanic || ""}
                              className="w-full text-black"
                            >
                              <option value="" disabled className="text-black">
                                Selecione um mecânico...
                              </option>
                              {mechanicOptions.map((option) => (
                                <option
                                  key={option.value}
                                  value={option.value}
                                  className="text-black"
                                >
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {!mechanic && (
                              <span className="text-red-600 text-sm ">
                                Mecânico é obrigatorio!
                              </span>
                            )}
                          </section>
                        </div>
                      }
                      onConfirm={async () => {
                        try {
                          const items = ticketsByPlate[row.licensePlate];
                          setLoadingNew(true);

                          if (!mechanic && items.some((i) => !i.mechanic)) {
                            notifyError(
                              "Selecione um mecânico para continuar."
                            );
                            setLoadingNew(false);
                            return;
                          }

                          for (const item of items) {
                            const mechToUse = mechanic || item.mechanic;

                            if (!mechToUse) {
                              notifyError(
                                "Selecione um mecânico para continuar."
                              );
                              setLoadingNew(false);
                              return;
                            }

                            if (mechanic && item.mechanic !== mechanic) {
                              await editWaitingTicketAndMechanic({
                                maintenanceId: item.maintenanceId,
                                mechanic,
                              });
                              item.mechanic = mechanic;
                            }

                            await processStockWithdrawal(item);
                          }

                          notifySuccess("Itens baixados com sucesso!");
                        } catch {
                          notifyError("Erro ao processar retirada.");
                        } finally {
                          await loadTicketStock();
                          setOpenPlate(null);
                          setLoadingNew(false);
                        }
                      }}
                      onClose={() => setOpenPlate(null)}
                    />
                  )}
                </div>
              );
            },
          }}
          entity="inventory-suppliers"
        />
        {loading && !loadingNew && <Loading />}

        {loadingNew && <LoadingOverlay />}
      </div>
    </Suspense>
  );
}

export default TableTicketStock;
